/**
 * Create Action Item Modal
 * Modal for creating new action items
 */
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useCreateActionItem } from '@/hooks/useActions'
import { useTeam } from '@/hooks/useTeam'
import type { CreateActionItemData } from '@/types/actions'

interface CreateActionItemModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultStatus?: string
}

export function CreateActionItemModal({
  open,
  onOpenChange,
  defaultStatus = 'backlog',
}: CreateActionItemModalProps) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateActionItemData>({
    defaultValues: {
      status: defaultStatus as any,
      priority: 'medium',
    },
  })
  const [selectedAssignee, setSelectedAssignee] = useState<number | null>(null)
  const [selectedPriority, setSelectedPriority] = useState('medium')
  
  const { toast } = useToast()
  const createMutation = useCreateActionItem()
  const { data: teamData } = useTeam()
  
  const onSubmit = async (data: CreateActionItemData) => {
    try {
      await createMutation.mutateAsync({
        ...data,
        status: defaultStatus as any,
        priority: selectedPriority as any,
        assignee: selectedAssignee || undefined,
      })
      
      toast({
        title: 'Action item created',
        description: 'The action item has been created successfully.',
      })
      
      reset()
      onOpenChange(false)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create action item. Please try again.',
        variant: 'destructive',
      })
    }
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create Action Item</DialogTitle>
          <DialogDescription>
            Add a new action item to {defaultStatus}.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              placeholder="Enter action item title..."
              {...register('title', { required: 'Title is required', maxLength: 200 })}
            />
            {errors.title && (
              <p className="text-sm text-destructive">{errors.title.message}</p>
            )}
          </div>
          
          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Enter description..."
              rows={4}
              {...register('description', { maxLength: 2000 })}
            />
          </div>
          
          {/* Priority */}
          <div className="space-y-2">
            <Label htmlFor="priority">Priority</Label>
            <Select value={selectedPriority} onValueChange={setSelectedPriority}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Assignee */}
          <div className="space-y-2">
            <Label htmlFor="assignee">Assignee</Label>
            <Select
              value={selectedAssignee?.toString() || 'unassigned'}
              onValueChange={(value) =>
                setSelectedAssignee(value === 'unassigned' ? null : parseInt(value))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {teamData?.members.map((member) => (
                  <SelectItem key={member.id} value={member.user.id.toString()}>
                    {member.user.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
